import * as THREE from "three";

export const MIN_STROBE_FREQUENCY_HZ = 0.5;
export const MAX_STROBE_FREQUENCY_HZ = 10;
export const DEFAULT_STROBE_FREQUENCY_HZ = 2;

type StrobeRuntimeData = {
  baseIntensity: number;
  frequencyHz: number;
};

const clampFrequencyHz = (value: unknown): number => {
  const frequency = Number(value);
  if (!Number.isFinite(frequency)) return DEFAULT_STROBE_FREQUENCY_HZ;
  return Math.min(MAX_STROBE_FREQUENCY_HZ, Math.max(MIN_STROBE_FREQUENCY_HZ, frequency));
};

const getStrobeRuntimeData = (light: THREE.PointLight): StrobeRuntimeData | null => {
  const data = light.userData?.artetraStrobe;
  if (!data) return null;
  return {
    baseIntensity: Number.isFinite(Number(data.baseIntensity)) ? Number(data.baseIntensity) : 1,
    frequencyHz: clampFrequencyHz(data.frequencyHz),
  };
};

export const configureStrobeLight = (
  light: THREE.PointLight,
  intensity: unknown,
  frequencyHz: unknown
) => {
  const baseIntensity = Number.isFinite(Number(intensity)) ? Number(intensity) : 1;
  light.intensity = baseIntensity;
  light.userData.artetraStrobe = {
    baseIntensity,
    frequencyHz: clampFrequencyHz(frequencyHz),
  } satisfies StrobeRuntimeData;
  return light;
};

export const getStrobeFrequencyHz = (light: THREE.PointLight): number =>
  getStrobeRuntimeData(light)?.frequencyHz ?? DEFAULT_STROBE_FREQUENCY_HZ;

export const updateStrobeLights = (
  lights: THREE.PointLight[],
  elapsedSeconds: number,
  flashingEnabled: boolean
): boolean => {
  let changed = false;
  lights.forEach((light) => {
    const data = getStrobeRuntimeData(light);
    if (!data) return;
    const phase = ((Math.max(0, elapsedSeconds) * data.frequencyHz) % 1 + 1) % 1;
    const nextIntensity = !flashingEnabled || phase < 0.5 ? data.baseIntensity : 0;
    if (light.intensity !== nextIntensity) {
      light.intensity = nextIntensity;
      changed = true;
    }
  });
  return changed;
};
